import { AuthState } from '@mse-reg/app/modules/login/auth.slice';
import { addExceptionNotification, addSuccess } from '@mse-reg/app/modules/notification.slice';
import { createAppThunk, RootState } from '@mse-reg/app/store';
import { addCommentToRegistrationInfo, removeCommentFromRegistrationInfo } from '@mse-reg/app/store/reducer-helpers';
import {
  CommentDto,
  IRegistrationClient,
  IRegistrationEditClient,
  IRegistrationExportClient,
  ModuleRegistrationInfoDto,
  RegistrationStateChangeDto,
  SemesterInfoDto,
  UasDto,
} from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';
import { SemesterFilterValues, semesterSelectionActions } from '../selections/semester-selection.slice';

export const getUas = (state: AuthState, id?: number) => {
  if (!state.allowedUas) {
    return null;
  }

  if (!id) {
    return state.allowedUas[0];
  }

  return state.allowedUas.find((u) => u.id === id);
};

// Async Thunks
const getUasRegistrationsAsync = createAppThunk(
  'uasRegistrations/FETCH_UAS_REGISTRATIONS',
  async (
    args: { uasId: number; semesterFilter: SemesterFilterValues },
    deps: { registrationClient: IRegistrationClient },
    dispatch,
    getState,
  ) => {
    const authState = (getState() as RootState).auth;
    dispatch(semesterSelectionActions.setSemesterFilter(args.semesterFilter));
    const uas = getUas(authState, args.uasId);
    dispatch(uasRegistrationsActions.selectUas(uas));

    const selectedSemester = (getState() as RootState).semesterSelection.selectedSemester;
    return await deps.registrationClient.getRegistrationsForUas(uas?.id, selectedSemester?.id);
  },
  (r) => ({ registrationClient: r.resolve<IRegistrationClient>('IRegistrationClient') }),
);

const deleteRegistrationsForUasAsync = createAppThunk(
  'uasRegistrations/DELETE_UAS_REGISTRATIONS',
  async (
    args: { semester: SemesterInfoDto; uasId: number },
    deps: { registrationClient: IRegistrationClient },
    dispatch,
  ) => {
    dispatch(uasRegistrationsActions.setMainContentLoading(true));
    await deps.registrationClient.deleteRegistrations(args.semester.id, args.uasId);
    await dispatch(getUasRegistrationsAsync({ uasId: args.uasId, semesterFilter: { semester: args.semester } }));
    dispatch(uasRegistrationsActions.setMainContentLoading(false));
  },
  (r) => ({ registrationClient: r.resolve<IRegistrationClient>('IRegistrationClient') }),
);

const exportAsync = createAppThunk(
  'uasRegistrations/DOWNLOAD',
  async (
    args: { uasId: number; semesterId: number },
    deps: { registrationExportClient: IRegistrationExportClient },
  ) => {
    const fileResponse = await deps.registrationExportClient.exportRegistrationsForUas(args.uasId, args.semesterId);
    saveAs(fileResponse.data, fileResponse.fileName);
    return fileResponse;
  },
  (r) => ({ registrationExportClient: r.resolve<IRegistrationExportClient>('IRegistrationExportClient') }),
);

const addCommentAsync = createAppThunk(
  'uasRegistrations/ADD_REGISTRATION_COMMENT',
  async (comment: CommentDto, deps: { registrationEditClient: IRegistrationEditClient }, dispatch) => {
    try {
      const id = await deps.registrationEditClient.addComment(comment);
      dispatch(addSuccess(`Successfully added comment.`));
      return { ...comment, id } as CommentDto;
    } catch (e) {
      dispatch(addExceptionNotification(e, 'An unknown error occurred while saving comment.'));
      return undefined;
    }
  },
  (r) => ({ registrationEditClient: r.resolve<IRegistrationEditClient>('IRegistrationEditClient') }),
);

const removeCommentAsync = createAppThunk(
  'uasRegistrations/REMOVE_REGISTRATION_COMMENT',
  async (comment: CommentDto, deps: { registrationEditClient: IRegistrationEditClient }, dispatch) => {
    try {
      await deps.registrationEditClient.deleteComment(comment.id);
      dispatch(addSuccess(`Successfully deleted comment.`));
      return comment;
    } catch (e) {
      dispatch(addExceptionNotification(e, 'An unknown error occurred while deleting comment.'));
      return undefined;
    }
  },
  (r) => ({ registrationEditClient: r.resolve<IRegistrationEditClient>('IRegistrationEditClient') }),
);

const getHistoryAsync = createAppThunk(
  'uasRegistrations/GET_HISTORY',
  async (moduleRegistrationId: number, deps: { registrationClient: IRegistrationClient }) => {
    return await deps.registrationClient.getHistory(moduleRegistrationId);
  },
  (r) => ({ registrationClient: r.resolve<IRegistrationClient>('IRegistrationClient') }),
);

const initialState = {
  loading: false,
  exportLoading: false,
  errorMessage: null as string | null,
  registrations: [] as ModuleRegistrationInfoDto[],
  selectedUas: null as UasDto,
  mainContentLoading: true,
};

export type UasRegistrationState = Readonly<typeof initialState>;

const slice = createSlice({
  name: 'uasRegistrations',
  initialState,
  reducers: {
    selectUas(state, action: PayloadAction<UasDto>) {
      state.selectedUas = action.payload;
    },
    setMainContentLoading(state, action: PayloadAction<boolean>) {
      state.mainContentLoading = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getUasRegistrationsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUasRegistrationsAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(getUasRegistrationsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.registrations = action.payload;
      })
      .addCase(deleteRegistrationsForUasAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteRegistrationsForUasAsync.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(deleteRegistrationsForUasAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportAsync.pending, (state) => {
        state.exportLoading = true;
      })
      .addCase(exportAsync.rejected, (state, action) => {
        state.exportLoading = false;
        state.errorMessage = action.error.message;
      })
      .addCase(exportAsync.fulfilled, (state) => {
        state.exportLoading = false;
      })
      .addCase(addCommentAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.registrations = addCommentToRegistrationInfo(state.registrations, action.payload);
        }
      })
      .addCase(removeCommentAsync.fulfilled, (state, action) => {
        if (action.payload) {
          state.registrations = removeCommentFromRegistrationInfo(state.registrations, action.payload);
        }
      });
  },
});

export const uasRegistrationsReducer = slice.reducer;
export const uasRegistrationsActions = slice.actions;
export const uasRegistrationsThunks = {
  getUasRegistrationsAsync,
  deleteRegistrationsForUasAsync,
  exportAsync,
  addCommentAsync,
  removeCommentAsync,
  getHistoryAsync,
};

// Legacy module compatibility
const initActions = (componentDispatch: any) => {
  return Object.freeze({
    setMainContentLoading: (loading: boolean) => componentDispatch(slice.actions.setMainContentLoading(loading)),
    selectUas: (uas: UasDto) => componentDispatch(slice.actions.selectUas(uas)),
    getUasRegistrationsAsync: async (uasId: number, filter: SemesterFilterValues) =>
      await componentDispatch(getUasRegistrationsAsync({ uasId, semesterFilter: filter })).unwrap(),
    deleteRegistrationsForUasAsync: async (semester: SemesterInfoDto, uasId: number) =>
      await componentDispatch(deleteRegistrationsForUasAsync({ semester, uasId })).unwrap(),
    exportAsync: async (uasId: number, semesterId: number) =>
      await componentDispatch(exportAsync({ uasId, semesterId })).unwrap(),
    addCommentAsync: async (comment: CommentDto) => await componentDispatch(addCommentAsync(comment)).unwrap(),
    removeCommentAsync: async (comment: CommentDto) => await componentDispatch(removeCommentAsync(comment)).unwrap(),
    getHistoryAsync: async (moduleRegistrationId: number): Promise<RegistrationStateChangeDto[]> =>
      await componentDispatch(getHistoryAsync(moduleRegistrationId)).unwrap(),
  });
};

export type UasRegistrationsActions = ReturnType<typeof initActions>;

export const uasRegistrationsModule = {
  initActions: (dispatch: any) => initActions(dispatch),
  reducer: slice.reducer,
};
